import type { FC } from "react";
import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button, Card, CardContent, Stack, TextField } from "@mui/material";
import { paths } from "src/paths";
import { thunks } from "src/thunks/lead-magnet";
import { useDispatch, useSelector } from "src/store";
import { RouterLink } from "src/components/router-link";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import LoadingButton from "@mui/lab/LoadingButton";
import { ComplexityType, LeadMagnet } from "src/types/lead-magnet";
import { useState } from "react";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    description: z
      .string()
      .min(1, t(tokens.general.validators.required) as string),
    type: z.string(),
    complexity: z.nativeEnum(ComplexityType),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

interface LeadMagnetEditFormProps {
  leadMagnet: LeadMagnet;
}

export const LeadMagnetEditForm: FC<LeadMagnetEditFormProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { leadMagnet } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { strategyId } = useSelector((state) => state.settings);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: leadMagnet.name,
      description: leadMagnet.description,
      type: leadMagnet.type,
      complexity: leadMagnet.complexity,
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const updatedLeadMagnet = await dispatch(
        thunks.updateLeadMagnet(strategyId!, leadMagnet.id, {
          name: data.name,
          description: data.description,
          type: data.type,
          complexity: data.complexity,
        })
      );
      if (updatedLeadMagnet) {
        toast.success(t(tokens.leadMagnets.details.form.successMessage));
      } else {
        toast.error(t(tokens.general.formError));
        setError("root", { message: "Update failed" });
      }
    } catch (err) {
      console.error(err);
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Update failed" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.name}
                    fullWidth
                    helperText={errors.name?.message}
                    label={t(tokens.leadMagnets.details.form.name)}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.description}
                    fullWidth
                    multiline
                    rows={5}
                    helperText={errors.description?.message}
                    label={t(tokens.leadMagnets.details.form.description)}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>
        <Stack
          alignItems="center"
          direction="row-reverse"
          justifyContent="flex-end"
          spacing={1}
        >
          <Button
            href={paths.leadMagnets.list}
            color="inherit"
            component={RouterLink}
          >
            {t(tokens.general.buttons.cancel)}
          </Button>
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.update)}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};
